// DateTimeInputField.tsx
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect } from "react";
import { type FormField } from "../formFieldType";
import MyBox from "../../MyBox";
import { Calendar, Clock } from "lucide-react";

export function DateTimeInputField({ field }: { field: FormField }) {
  const { register, setValue, control, formState } = useFormContext();

  const value = useWatch({
    control,
    name: field.name,
  });

  const error = formState.errors[field.name];
  const minDateTime = field.min ? new Date(field.min).toISOString().slice(0, 16) : undefined;
  const maxDateTime = field.max ? new Date(field.max).toISOString().slice(0, 16) : undefined;

  return (
    <MyBox topText={field.label} required={field.required}>
      <div className="space-y-3">
        <div className="relative">
          <input
            type="datetime-local"
            value={value || ''}
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
            })}
            disabled={field.disabled}
            min={minDateTime}
            max={maxDateTime}
            className={`
              w-full px-3 py-2 pl-10
              border rounded-lg
              bg-white dark:bg-gray-800
              text-gray-900 dark:text-gray-100
              focus:outline-none focus:ring-2 focus:ring-blue-500
              transition-all duration-200
              ${error 
                ? "border-red-500 dark:border-red-400 focus:ring-red-500" 
                : "border-gray-300 dark:border-gray-600 focus:border-blue-500"
              }
              ${field.disabled ? "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-900" : ""}
            `}
          />
          <Calendar className={`
            absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none
            ${error ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'}
          `} />
        </div>

        {/* Helper Text */}
        {field.helperText && !error && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {field.helperText}
          </p>
        )}

        {/* Error Message */}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400">
            {String(error.message)}
          </p>
        )}
      </div>
    </MyBox>
  );
}